import { RENDER_ORDER } from "@graphic/constants";

import { SpaceObjectType, SpaceObjectInstance } from "@graphic/types";
import {
  convertToColorNumber,
  getColorByType,
  getColorNumberByType,
  getCurveColorByType,
  getOrbitPositions,
  setLineWidthByDistance
} from "@graphic/utils";
import { makeLine } from "@graphic/utils/three-object";
import { useFrame, useThree } from "@react-three/fiber";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import * as THREE from "three";
import { Line2 } from "three-stdlib";

import { useTimeStore } from "@graphic/stores";

export type CurveType = "default" | "primary" | "secondary";

type CurveProps = {
  data: SpaceObjectType;
  type?: CurveType;
  subtype?: string;
};

export type CurveHandle = {
  setVisible: (visible: boolean) => void;
};

export const Curve = ({ data, type, subtype }: CurveProps, ref) => {
  const get = useThree(state => state.get);
  const curveRef = useRef<Line2 | null>(null);
  const center = new THREE.Vector3(0, 0, 0);

  // const color = getCurveColorByType(type);

  useImperativeHandle(ref, () => ({
    setVisible(visible: boolean) {
      if (!curveRef.current) return;
      curveRef.current.visible = visible;
    }
  }));

  useEffect(() => {
    if (!data) return;
    addCurve();
    return () => {
      removeCurve();
    };
  }, [data]);

  useFrame(({ camera }) => {
    if (!curveRef.current) return;
    if (!curveRef.current.visible) return;
    const distance = center.distanceTo(camera.position);
    const width = setLineWidthByDistance(distance);

    const time = useTimeStore.getState().time;
    setCurveWidth(width);
    setCurvePosition(time);
  }, RENDER_ORDER.CURVE);

  // useFrame(() => {
  //   if (!lineRef || !lineRef.current || !curveRef || !curveRef.current) return;
  //   curveRef.current.visible = lineRef.current.visible;
  // }, RENDER_ORDER.VISIBLE);

  const setCurveWidth = (width: number) => {
    if (!curveRef.current) return;

    curveRef.current.material.linewidth = width;
    curveRef.current.material.needsUpdate = true;
  };

  const addCurve = () => {
    const { satrec } = data;
    const defaultTime = useTimeStore.getState().time;
    const positions = getOrbitPositions(satrec, defaultTime);
    // const curveColor = convertToColorNumber(color);
    const colorNumber = getColorNumberByType(subtype);
    const curve = makeLine(positions, colorNumber);

    curveRef.current = curve;
    const scene = get().scene;

    scene.add(curve);
  };

  const setCurvePosition = (time: Dayjs) => {
    const { satrec } = data;
    const positions = getOrbitPositions(satrec, time);

    curveRef.current.geometry.setPositions(positions);

    curveRef.current.geometry.attributes.position.needsUpdate = true;
  };

  const removeCurve = () => {
    if (curveRef.current) {
      const scene = get().scene;
      scene.remove(curveRef.current);
      curveRef.current.geometry.dispose();
      curveRef.current.material.dispose();
    }
  };

  return null;
};

export default forwardRef(Curve);
